import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, Subscription, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";

import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import {
  InterestType,
  Portfolio,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { SwapCetip } from "../../model/domain/SwapCetip";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { CounterpartComponent } from "../counterpart/counterpart.component";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-swap-cetip",
  templateUrl: "./swap-cetip.component.html",
  styleUrls: ["./swap-cetip.component.css"],
})
export class SwapCetipComponent implements OnInit, OnDestroy {
  public model: SwapCetip = null;

  navigationSubscription: Subscription;

  isEditing = false;
  canValidate = false;

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("counterpart", { static: true })
  counterpartComponent: CounterpartComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  TicketTypeEnum = TicketType;

  ListaTipoJuros: NamedType<number>[];
  ListaBaseIndexador: NamedType<number>[];

  dataChanged: EventEmitter<string>;
  isNotProduction = environment.name != 'production';

  constructor(
    private service: TicketService,
    public validator: ValidatorService,
    private dialogService: DialogService,
    private authService: AuthService,
    private snackBarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private location: Location,
    private loaderService: LoaderService,
    private cutoffService: CutoffService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  validators = {
    operationValue: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.operationValueMask, {}),
    activeIndexPercent: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.decimalIndexFeeMask, {}),
    activeIndexTax: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.decimalIndexFeeMask, {}),
    passiveIndexPercent: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.decimalIndexFeeMask, {}),
    passiveIndexTax: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.decimalIndexFeeMask, {}),
    activeInterestType: (value) =>
      [InterestType.Exponencial, InterestType.Linear].includes(value),
    passiveInterestType: (value) =>
      [InterestType.Exponencial, InterestType.Linear].includes(value),
    activeIndexBase: (value) => [1, 2].includes(value),
    passiveIndexBase: (value) => [1, 2].includes(value),
    expirationDate: (value) => this.model.expirationDate != null && value != "",
  };

  ngOnInit() {
    this.dataChanged = new EventEmitter<string>();
    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.frm.resetForm();
    this.model = new SwapCetip();

    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();

    this.annotationComponent.annotation = null;

    this.isEditing = false;
    this.canValidate = false;

    this.model.command = "0";
    this.model.id = 0;
    this.model.operationDate = new Date();
    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundoComponent.setEnable(true);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundoComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null) {
    this.model = new SwapCetip();
    this.ListaBaseIndexador = this.service.ListFoundationIndexes();
    this.ListaTipoJuros = this.service.ListInterestTypes();

    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;
    this.model.id = id;
    this.model.command = "0";

    if (id) {
      this.service.SwapCETIP.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.model.portfolioDocument = this.validator.formatDocument(
            this.model.portfolioDocument
          );
          this.model.counterpartDocument = this.validator.formatDocument(
            this.model.counterpartDocument
          );

          this.model.operationValue = this.decimalPipe.transform(
            this.model.operationValue,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.activeIndexPercent = this.decimalPipe.transform(
            this.model.activeIndexPercent,
            "1.2-2",
            "pt-BR"
          ) as any;
          this.model.activeIndexTax = this.decimalPipe.transform(
            this.model.activeIndexTax,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.passiveIndexPercent = this.decimalPipe.transform(
            this.model.passiveIndexPercent,
            "1.2-2",
            "pt-BR"
          ) as any;
          this.model.passiveIndexTax = this.decimalPipe.transform(
            this.model.passiveIndexTax,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.expirationDate = this.datePipe.transform(
            this.model.expirationDate,
            "yyyy-MM-dd"
          ) as any;

          this.fundoComponent.fillComponentFieldsLoad(this.model);

          this.counterpartComponent.fillComponentFieldsLoad(this.model);

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.SwapCetip])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  submit() {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();
    const counterpartErrors = this.counterpartComponent.getErrors();

    if (f.invalid || errors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    if (fundErrors.length > 0) {
      return throwError("Verifique os campos nos dados do Fundo Investidor.");
    }

    if (counterpartErrors.length > 0) {
      return throwError("Verifique os campos nos dados da Contraparte.");
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.operationValue = this.validator.getCurrency(data.operationValue);
    data.activeIndexTax = this.validator.getCurrency(data.activeIndexTax);
    data.activeIndexPercent = this.validator.getCurrency(
      data.activeIndexPercent
    );
    data.passiveIndexTax = this.validator.getCurrency(data.passiveIndexTax);
    data.passiveIndexPercent = this.validator.getCurrency(
      data.passiveIndexPercent
    );

    data.counterpartDocument = this.validator.sanitize(
      data.counterpartDocument
    );

    data.expirationDate = new Date(
      data.expirationDate
        .toString()
        .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
    );

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data = this.counterpartComponent.fillCounterpartData(
      data,
      this.counterpartComponent.counterpartModel
    );

    data.annotations = this.annotationComponent.annotation;

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.SwapCETIP.updateTicket(this.model.id, data)
        : this.service.SwapCETIP.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((swapCetip: SwapCetip) => {
          if (!this.isEditing) {
            this.model.id = swapCetip.id;
            this.model.operationDate = swapCetip.operationDate;
            this.model.statusId = swapCetip.statusId;
            this.model.accountManagerName = swapCetip.accountManagerName;
            this.model.accountManagerDocument =
              swapCetip.accountManagerDocument;
            this.model.ticketCutoffTypeId = swapCetip.ticketCutoffTypeId;
            this.model.portfolioFundId = swapCetip.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (swapCetip: SwapCetip) => {
            this.service.SwapCETIP.forwardTicket(swapCetip.id).subscribe(
              (res) => {
                this.model.statusDescription = res.statusDescription;

                this.setEnable(false);

                this.snackBarService.open("Boleta encaminhada.", "OK", {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                });

                this.getTicket(swapCetip.id);
              }
            );
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.SwapCETIP.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve() {
    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service.approve(this.model.id, "").subscribe(() => {
            this.snackBarService.open("Boleta atualizada com sucesso.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            this.getTicket();
          });
        }
      });
  }

  reject() {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );
              this.getTicket();
            });
        }
      });
  }

  duplicate() {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.SwapCetip },
          ]);
        });
    });
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string) {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.SwapCetip;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketFundQuota) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }
}
